import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox, Divider, FormControl, FormControlLabel, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type { Condition } from '@repo/api-gw-sdk';
import { useState, type CSSProperties, type UIEventHandler } from 'react';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { HelpIcon } from '@/components/shared/helpIcon';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useFacetOptions } from '@/contexts/useFilterFacets';
import { PropertyType, isValueCondition, type FilterProperty, type FilterProps } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BACKUP_STATUS_PROPERTY, CONTROL_ID_PROPERTY, CONTROL_LEVEL_PROPERTY, MUTED_CONTROL_ID_PROPERTY } from './propertyNames';
import { BackupStatusFilterRow, AdditionalStatuses, ViolationsDetectedFilterRow } from '../backupStatus/backupStatusFilterRow';
import { SearchTextBox } from '../search/searchTextBox';
import { TransactionalTextField } from '../shared/transactionalTextField';
import { Virtuoso } from '../shared/virtuoso';
import { TextDetailsTuple } from '../text/textDetailsTuple';
export type { FilterProps };
export const FreeTextFilter = t0 => {
  const $ = _c(36);
  const {
    placeholder,
    property,
    conditions,
    onChange,
    "data-testid": dataTestId,
    showSearchIcon: t1,
    operator: t2
  } = t0;
  const showSearchIcon = t1 === undefined ? true : t1;
  const operator = t2 === undefined ? ConditionOperatorEnum.Contains : t2;
  let T0;
  let T1;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== conditions || $[1] !== dataTestId || $[2] !== placeholder || $[3] !== property || $[4] !== showSearchIcon) {
    let t11;
    if ($[15] !== property) {
      t11 = x => isValueCondition(x) && x.property === property.name;
      $[15] = property;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    const condition = conditions.find(t11);
    T1 = FormControl;
    t9 = "outlined";
    t10 = true;
    T0 = TransactionalTextField;
    t3 = showSearchIcon;
    t4 = true;
    t5 = placeholder;
    t6 = true;
    t7 = dataTestId;
    t8 = isValueCondition(condition) && condition?.value?.[0] || "";
    $[0] = conditions;
    $[1] = dataTestId;
    $[2] = placeholder;
    $[3] = property;
    $[4] = showSearchIcon;
    $[5] = T0;
    $[6] = T1;
    $[7] = t10;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    T0 = $[5];
    T1 = $[6];
    t10 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[17] !== conditions || $[18] !== onChange || $[19] !== operator || $[20] !== property) {
    t11 = value => {
      const cleanValue = (value || "").trim();
      onChange(conditions.filter(x_0 => isValueCondition(x_0) && x_0.property !== property.name).concat(cleanValue ? [{
        property: property.name,
        operator,
        value: [cleanValue]
      }] : []));
    };
    $[17] = conditions;
    $[18] = onChange;
    $[19] = operator;
    $[20] = property;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== T0 || $[23] !== t11 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t12 = <T0 showSearchIcon={t3} showClearIcon={t4} placeholder={t5} allowEmptyValue={t6} data-testid={t7} initValue={t8} onChange={t11} />;
    $[22] = T0;
    $[23] = t11;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== T1 || $[32] !== t10 || $[33] !== t12 || $[34] !== t9) {
    t13 = <T1 variant={t9} fullWidth={t10}>{t12}</T1>;
    $[31] = T1;
    $[32] = t10;
    $[33] = t12;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
};
interface OptionType {
  id: string;
  name?: string;
  Image?: React.ReactNode;
  description?: React.ReactNode;
  isGroupTitle?: boolean;
  hideName?: boolean;
}
export const MultiSelectFilter = t0 => {
  const $ = _c(32);
  const {
    property,
    conditions,
    onChange,
    options: rawOptions,
    "data-testid": dataTestId,
    showSelectAll,
    virtuosoStyles,
    onVirtuosoScroll,
    showNameFirst,
    shouldShowId
  } = t0;
  const options = useFacetOptions(property.name, rawOptions);
  let allOptions;
  let onItemChange;
  let selectedResources;
  let t1;
  if ($[0] !== conditions || $[1] !== dataTestId || $[2] !== onChange || $[3] !== options || $[4] !== property || $[5] !== shouldShowId || $[6] !== showNameFirst || $[7] !== showSelectAll) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t2;
      if ($[12] !== property.name) {
        t2 = x => isValueCondition(x) && x.property === property.name;
        $[12] = property.name;
        $[13] = t2;
      } else {
        t2 = $[13];
      }
      const condition = conditions.find(t2);
      selectedResources = isValueCondition(condition) && condition?.value || [];
      onItemChange = (option, newValue) => {
        const values = newValue ? [...selectedResources, option.id] : selectedResources.filter(x_0 => x_0 !== option.id);
        const existingCondition = conditions.find(x_1 => isValueCondition(x_1) && x_1.property === property.name);
        onChange(conditions.filter(x_2 => x_2 !== existingCondition).concat(values.length ? [{
          property: property.name,
          operator: existingCondition ? existingCondition.operator : property.type === PropertyType.MultipleValues ? ConditionOperatorEnum.ContainsAnyOf : ConditionOperatorEnum.In,
          value: values
        }] : []));
      };
      allOptions = showSelectAll ? [{
        id: "select-all"
      }, ...options] : options;
      if (allOptions.length < 100) {
        let t3;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = {
            gap: "4px"
          };
          $[14] = t3;
        } else {
          t3 = $[14];
        }
        t1 = <Stack sx={t3}>{allOptions.map(option_0 => option_0.id === "select-all" ? <SelectAllFilterItem key={option_0.id} options={options} selectedResources={selectedResources} onChange={onChange} conditions={conditions} property={property} /> : <MultiSelectFilterItem key={option_0.id} option={option_0} dataTestId={dataTestId} onChange={newValue_0 => onItemChange(option_0, newValue_0)} isChecked={selectedResources.includes(option_0.id)} shouldShowId={!!shouldShowId} showNameFirst={!!showNameFirst} />)}</Stack>;
        break bb0;
      }
    }
    $[0] = conditions;
    $[1] = dataTestId;
    $[2] = onChange;
    $[3] = options;
    $[4] = property;
    $[5] = shouldShowId;
    $[6] = showNameFirst;
    $[7] = showSelectAll;
    $[8] = allOptions;
    $[9] = onItemChange;
    $[10] = selectedResources;
    $[11] = t1;
  } else {
    allOptions = $[8];
    onItemChange = $[9];
    selectedResources = $[10];
    t1 = $[11];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  if ($[15] !== virtuosoStyles) {
    t2 = virtuosoStyles || {
      height: "100%"
    };
    $[15] = virtuosoStyles;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== conditions || $[18] !== dataTestId || $[19] !== onChange || $[20] !== onItemChange || $[21] !== options || $[22] !== property || $[23] !== selectedResources || $[24] !== shouldShowId || $[25] !== showNameFirst) {
    t3 = (index, option_1) => option_1.id === "select-all" ? <SelectAllFilterItem key={option_1.id} options={options} selectedResources={selectedResources} onChange={onChange} conditions={conditions} property={property} /> : <MultiSelectFilterItem key={option_1.id} option={option_1} dataTestId={dataTestId} onChange={newValue_1 => onItemChange(option_1, newValue_1)} isChecked={selectedResources.includes(option_1.id)} shouldShowId={!!shouldShowId} showNameFirst={!!showNameFirst} />;
    $[17] = conditions;
    $[18] = dataTestId;
    $[19] = onChange;
    $[20] = onItemChange;
    $[21] = options;
    $[22] = property;
    $[23] = selectedResources;
    $[24] = shouldShowId;
    $[25] = showNameFirst;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  let t4;
  if ($[27] !== allOptions || $[28] !== onVirtuosoScroll || $[29] !== t2 || $[30] !== t3) {
    t4 = <Virtuoso style={t2} data={allOptions} onScroll={onVirtuosoScroll} itemContent={t3} />;
    $[27] = allOptions;
    $[28] = onVirtuosoScroll;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  return t4;
};
const SelectAllFilterItem = t0 => {
  const $ = _c(18);
  const {
    options,
    selectedResources,
    onChange,
    conditions,
    property
  } = t0;
  let t1;
  if ($[0] !== options) {
    t1 = options.filter(_temp);
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectableOptions = t1;
  const t2 = `Select all (${selectableOptions.length})`;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      id: "select-all",
      name: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== conditions || $[5] !== onChange || $[6] !== property || $[7] !== selectableOptions) {
    t4 = newValue => {
      const values = newValue ? selectableOptions.map(_temp2) : [];
      onChange(conditions.filter(x_1 => isValueCondition(x_1) && x_1.property !== property.name).concat(values.length ? [{
        property: property.name,
        operator: property.type === PropertyType.MultipleValues ? ConditionOperatorEnum.ContainsAnyOf : ConditionOperatorEnum.In,
        value: values
      }] : []));
    };
    $[4] = conditions;
    $[5] = onChange;
    $[6] = property;
    $[7] = selectableOptions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== options || $[10] !== selectedResources) {
    let t6;
    if ($[12] !== selectedResources) {
      t6 = x_2 => selectedResources.includes(x_2.id);
      $[12] = selectedResources;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = options.every(t6);
    $[9] = options;
    $[10] = selectedResources;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <MultiSelectFilterItem shouldShowId={false} showNameFirst={false} option={t3} onChange={t4} isChecked={t5} />;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const MultiSelectFilterItem = t0 => {
  const $ = _c(31);
  const {
    option,
    dataTestId,
    onChange,
    isChecked,
    shouldShowId,
    showNameFirst
  } = t0;
  if (option.isGroupTitle) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        paddingY: "8px"
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== option.name) {
      t2 = <Typography variant="overline" sx={t1}>{option.name}</Typography>;
      $[1] = option.name;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      display: "flex",
      width: "100%"
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `${dataTestId ? `${dataTestId}-` : ""}${option.id}`;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      "data-testid": t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = t3 as Record<string, string>;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      input: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onChange) {
    t6 = (event, value) => onChange(value);
    $[8] = onChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isChecked || $[11] !== t5 || $[12] !== t6) {
    t7 = <Checkbox slotProps={t5} className="p-[8px] mr-[4px]" size="small" checked={isChecked} onChange={t6} />;
    $[10] = isChecked;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      alignItems: "center",
      gap: "12px"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== option.hideName || $[16] !== option.id || $[17] !== option.name || $[18] !== shouldShowId || $[19] !== showNameFirst) {
    t9 = !option.hideName && <TextDetailsTuple primaryProp={shouldShowId ? showNameFirst ? option.name : option.id : undefined} secondaryProp={showNameFirst ? option.id : option.name} />;
    $[15] = option.hideName;
    $[16] = option.id;
    $[17] = option.name;
    $[18] = shouldShowId;
    $[19] = showNameFirst;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== option.description) {
    t10 = option.description && <HelpIcon title={option.description} className="absolute right-0 cursor-default" tooltipProps={{
      slotProps: {
        tooltip: {
          sx: {
            padding: "16px"
          }
        }
      }
    }} />;
    $[21] = option.description;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== option.Image || $[24] !== t10 || $[25] !== t9) {
    t11 = <Stack direction="row" className="w-[216px] relative" sx={t8}>{option.Image}{t9}{t10}</Stack>;
    $[23] = option.Image;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== option.id || $[28] !== t11 || $[29] !== t7) {
    t12 = <FormControlLabel key={option.id} className="group/help" sx={t1} control={t7} label={t11} />;
    $[27] = option.id;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};
export const BackupStatusFilter = ({
  conditions,
  onChange,
  renderDrillDownFilter,
  showAdditionalStatuses
}: FilterProps & {
  showAdditionalStatuses: boolean;
}) => {
  const {
    dlsgFrontend
  } = useFeatureFlags();
  const dal = useDAL();
  const {
    data: hasControls
  } = dal.controls.useHasControls();
  if (!renderDrillDownFilter) {
    return null;
  }
  const valueConditions = conditions.filter(isValueCondition);
  const backupStatusConditions = valueConditions.filter(x => x.property === BACKUP_STATUS_PROPERTY);
  const includedBackupStatusCondition = backupStatusConditions.find(x_0 => x_0.operator === ConditionOperatorEnum.In);
  const excludedBackupStatusCondition = backupStatusConditions.find(x_1 => x_1.operator === ConditionOperatorEnum.NotIn);
  const controlLevelCondition = valueConditions.find(x_2 => x_2.property === CONTROL_LEVEL_PROPERTY);
  const selectedStatuses = includedBackupStatusCondition?.value || [];
  const selectedLevel = controlLevelCondition?.value?.[0] || undefined;
  const toggleBackupStatus = (status: BackupStatus) => {
    const cleanConditions = valueConditions.filter(x_3 => x_3 !== includedBackupStatusCondition && x_3.property !== CONTROL_LEVEL_PROPERTY && x_3.property !== CONTROL_ID_PROPERTY && x_3.property !== MUTED_CONTROL_ID_PROPERTY);
    if (selectedStatuses.includes(status)) {
      const updatedStatuses = selectedStatuses.filter(x_4 => x_4 !== status);
      onChange(cleanConditions.concat(updatedStatuses.length === 0 ? [] : [{
        property: BACKUP_STATUS_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: updatedStatuses
      }]));
    } else {
      onChange(cleanConditions.concat({
        property: BACKUP_STATUS_PROPERTY,
        operator: ConditionOperatorEnum.In,
        value: selectedStatuses.concat([status])
      }));
    }
  };
  const selectControlLevel = (severity: string) => {
    const cleanConditions_0 = valueConditions.filter(x_5 => x_5 !== includedBackupStatusCondition && x_5.property !== CONTROL_LEVEL_PROPERTY && x_5.property !== CONTROL_ID_PROPERTY && x_5.property !== MUTED_CONTROL_ID_PROPERTY);
    onChange(cleanConditions_0.concat({
      property: CONTROL_LEVEL_PROPERTY,
      operator: ConditionOperatorEnum.ContainsAnyOf,
      value: [severity.toUpperCase()]
    }));
  };
  return <Stack>
      <BackupStatusFilterRow backupStatus={BackupStatus.Protected} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      {dlsgFrontend && <BackupStatusFilterRow backupStatus={BackupStatus.Dlsg} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />}
      {hasControls && <>
          <BackupStatusFilterRow backupStatus={BackupStatus.AllViolationsMuted} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
          <ViolationsDetectedFilterRow backupStatus={BackupStatus.ViolationsDetected} toggleBackupStatus={toggleBackupStatus} selectControlLevel={selectControlLevel} selectedBackupStatuses={selectedStatuses} selectedLevel={selectedLevel?.toLowerCase()} renderDrillDownFilter={renderDrillDownFilter} conditions={conditions} onFilterChange={onChange} />
        </>}
      <BackupStatusFilterRow backupStatus={BackupStatus.ActionRequired} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      <Divider sx={{
      margin: '12px -24px'
    }} />
      <BackupStatusFilterRow backupStatus={BackupStatus.GenericBackups} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      <BackupStatusFilterRow backupStatus={BackupStatus.NotBackedUp} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      <BackupStatusFilterRow backupStatus={BackupStatus.InitialClassification} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      <BackupStatusFilterRow backupStatus={BackupStatus.Terminated} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />
      {!excludedBackupStatusCondition?.value?.includes(BackupStatus.Disconnected) && <BackupStatusFilterRow backupStatus={BackupStatus.Disconnected} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />}
      {!excludedBackupStatusCondition?.value?.includes(BackupStatus.ExcludedFromBackup) && <BackupStatusFilterRow backupStatus={BackupStatus.ExcludedFromBackup} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />}
      {!excludedBackupStatusCondition?.value?.includes(BackupStatus.Unsupported) && <BackupStatusFilterRow backupStatus={BackupStatus.Unsupported} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />}
      {!excludedBackupStatusCondition?.value?.includes(BackupStatus.LimitExceeded) && <BackupStatusFilterRow backupStatus={BackupStatus.LimitExceeded} toggleBackupStatus={toggleBackupStatus} selectedBackupStatuses={selectedStatuses} />}
      {showAdditionalStatuses && <>
          <Divider sx={{
        margin: '12px -24px'
      }} />
          <AdditionalStatuses valueConditions={valueConditions} onChange={onChange} excludedBackupStatusCondition={excludedBackupStatusCondition} includedBackupStatusCondition={includedBackupStatusCondition} />
        </>}
    </Stack>;
};
export const FullPanelMultiSelectFilter = props => {
  const $ = _c(19);
  const [filter, setFilter] = useState("");
  if (props.isLoading) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Loader />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== filter || $[2] !== props) {
    let t1;
    if ($[4] !== props.property) {
      t1 = x => isValueCondition(x) && x.property === props.property.name;
      $[4] = props.property;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const condition = props.conditions.find(t1);
    const selectedResources = isValueCondition(condition) && condition?.value || [];
    let filteredOptions;
    let t2;
    if ($[6] !== filter || $[7] !== props.options || $[8] !== props.shouldShowId) {
      let t3;
      if ($[11] !== filter || $[12] !== props.shouldShowId) {
        t3 = x_0 => !filter || x_0.name?.toLowerCase().includes(filter.toLowerCase()) || props.shouldShowId && x_0.id?.toLowerCase().includes(filter.toLowerCase());
        $[11] = filter;
        $[12] = props.shouldShowId;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      filteredOptions = props.options.filter(t3);
      t2 = filteredOptions.filter(_temp3);
      $[6] = filter;
      $[7] = props.options;
      $[8] = props.shouldShowId;
      $[9] = filteredOptions;
      $[10] = t2;
    } else {
      filteredOptions = $[9];
      t2 = $[10];
    }
    const filteredOptionsCount = t2.length;
    let t3;
    if ($[14] !== filter || $[15] !== filteredOptions || $[16] !== filteredOptionsCount || $[17] !== props) {
      t3 = <ScrollableWrapper><FullPanelMultiSelectFilterBody {...props} filter={filter} setFilter={setFilter} filteredOptions={filteredOptions} filteredOptionsCount={filteredOptionsCount} /></ScrollableWrapper>;
      $[14] = filter;
      $[15] = filteredOptions;
      $[16] = filteredOptionsCount;
      $[17] = props;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    t0 = <Stack className="h-full overflow-hidden">{t3}{!!selectedResources.length && <Stack direction="row" className="px-[24px] py-[16px]" sx={{
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "var(--mui-customShadows-panelUp)"
      }}><Typography>{`${selectedResources.length} ${pluralize(props.entityName, selectedResources.length)} selected`}</Typography><Stack direction="row" sx={{
          alignItems: "center"
        }}><Tooltip title="Clear all"><IconButton onClick={() => props.onChange(props.conditions.filter(x_2 => x_2 !== condition))}><i className="material-symbols-filter-alt-off-outline-rounded text-(--mui-palette-text-primary) h-[20px] w-[20px]" /></IconButton></Tooltip><Button className="ml-[12px]" variant="outlined" onClick={() => props.onDoneClick()}>Done</Button></Stack></Stack>}</Stack>;
    $[1] = filter;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const FullPanelMultiSelectFilterBody = props => {
  const $ = _c(18);
  const {
    isScrolledVertically,
    setIsScrolledVertically
  } = useScrollable();
  let t0;
  if ($[0] !== isScrolledVertically || $[1] !== setIsScrolledVertically) {
    t0 = event => {
      const target = event.target as HTMLDivElement;
      if (typeof target.scrollTop === "number") {
        const isScrolled = target.scrollTop > 0;
        if (isScrolled !== isScrolledVertically) {
          setIsScrolledVertically(isScrolled);
        }
      }
    };
    $[0] = isScrolledVertically;
    $[1] = setIsScrolledVertically;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onScroll = t0;
  let t1;
  if ($[3] !== props.filter || $[4] !== props.hideSearch || $[5] !== props.setFilter) {
    t1 = !props.hideSearch && <ScrollableHeader className="px-[24px] pb-[24px]"><SearchTextBox value={props.filter} onChange={props.setFilter} placeholder="Search by name or ID" /></ScrollableHeader>;
    $[3] = props.filter;
    $[4] = props.hideSearch;
    $[5] = props.setFilter;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== props.filteredOptionsCount) {
    t2 = props.filteredOptionsCount === 0 && <Typography variant="body1" className="my-[12px]">{"No matching values found. Try a new search term."}</Typography>;
    $[7] = props.filteredOptionsCount;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== onScroll || $[10] !== props) {
    t3 = props.filteredOptionsCount > 0 && <MultiSelectFilter {...props} showSelectAll={!props.hideSearch && !!props.filter} options={props.filteredOptions} onVirtuosoScroll={onScroll} showNameFirst={props.showNameFirst} shouldShowId={props.shouldShowId} />;
    $[9] = onScroll;
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <ScrollableContent className="px-[24px] py-[12px]">{t2}{t3}</ScrollableContent>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[15] = t1;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
function _temp(x) {
  return !x.isGroupTitle;
}
function _temp2(x_0) {
  return x_0.id;
}
function _temp3(x_1) {
  return !x_1.isGroupTitle;
}